import { GoogleGenAI, Type, Schema } from "@google/genai";
import { ProductData } from "../types";

export interface BatchAnalysisItem {
  imageIndices: number[];
  productData: Partial<ProductData>;
}

export const analyzeProductImagesBatch = async (base64Images: string[]): Promise<BatchAnalysisItem[]> => {
  if (base64Images.length === 0) return [];

  const batchSchema: Schema = {
    type: Type.ARRAY,
    items: {
      type: Type.OBJECT,
      properties: {
        imageIndices: {
          type: Type.ARRAY,
          items: { type: Type.NUMBER },
          description: "Indices of images that belong to this product (e.g. [0, 2] if images 0 and 2 are the same product from different angles).",
        },
        title: {
          type: Type.STRING,
          description: "The EXACT real product name as identified from labels, packaging, or tags in the image. NEVER make up generic titles.",
        },
        description: {
          type: Type.STRING,
          description: "A proper product description (2-3 sentences). Write it like a seller would: what the product is, what it does, key features, benefits, materials. NEVER describe the image or photo itself. NEVER start with 'This image shows...' or 'I can see...'.",
        },
        price: {
          type: Type.NUMBER,
          description: "A realistic estimated market price in Nigerian Naira (NGN). Example: 5000 for ₦5,000.",
        },
        category: {
          type: Type.STRING,
          description: "The actual category of the product (e.g., Fashion, Electronics, Beauty, Home, Books, Digital, Service). Be specific.",
        },
        tags: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "3-5 specific, relevant tags describing the actual product.",
        },
        brand: { type: Type.STRING, description: "The ACTUAL brand name visible on the product or packaging. Only use 'Generic' if absolutely no brand is visible anywhere." },
        condition: {
          type: Type.STRING,
          enum: ["New", "Used", "Refurbished"],
          description: "The likely condition of the item based on visual cues.",
        },
        colors: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Visual colors detected.",
        },
        sizes: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Likely sizes if apparel (e.g. S, M, L).",
        },
        materials: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Likely materials (e.g. Cotton, Leather, Metal).",
        },
        styles: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Style descriptors (e.g. Vintage, Modern, Streetwear).",
        },
        patterns: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Pattern descriptors (e.g. Striped, Solid, Floral).",
        },
        isDigital: {
          type: Type.BOOLEAN,
          description: "True if this looks like a digital product, software, or service card.",
        },
        dimensions: { type: Type.STRING, description: "Estimated dimensions if applicable (e.g. '10x10cm'), or empty." },
        weight: { type: Type.STRING, description: "Estimated weight if applicable (e.g. '0.5kg'), or empty." },
      },
      required: ["imageIndices", "title", "description", "price", "category", "tags"],
    },
  };

  const strippedImages = base64Images.map(img => img.replace(/^data:image\/\w+;base64,/, ""));

  let lastError: Error | null = null;

  for (let i = 0; i < apiClients.length; i++) {
    const { client, name } = apiClients[i];
    try {
      const parts: any[] = strippedImages.map((data, idx) => ({
        inlineData: { mimeType: "image/jpeg", data },
      }));
      parts.push({
        text: `You are Meo, an expert Nigerian social commerce listing generator.

Analyze the image to extract product details, then output a SALES DESCRIPTION — NOT an image analysis.

IMPORTANT RULES:
- Title: The exact real product name from labels or physical features. Never generic.
- Brand: The actual brand. Only "Generic" if truly unbranded.
- Description: Write a compelling product description a seller would post. Describe what the product IS, what it DOES, its features, materials, and benefits. NEVER describe the photo itself. NEVER start with "This image shows", "I can see", "The photo depicts", etc.
  ✓ GOOD: "Premium wireless noise-cancelling headphones with 30-hour battery life. Soft over-ear cushions for all-day comfort. Features Bluetooth 5.0 and built-in microphone."
  ✗ BAD: "This image shows a pair of black headphones. I can see they have cushioned ear pads and appear to be wireless."

Grouping: Some images may be different angles of the SAME product. Group those using imageIndices.

${base64Images.length} image(s). Price in NGN.`,
      });

      const response = await client.models.generateContent({
        model: "gemini-2.5-flash",
        contents: { parts },
        config: {
          responseMimeType: "application/json",
          responseSchema: batchSchema,
        },
      });

      const text = response.text;
      if (!text) throw new Error("No text returned from Gemini");

      const data = JSON.parse(text);
      if (!Array.isArray(data)) throw new Error("Expected array response");

      console.log(`Batch analysis successfully used ${name} API key`);
      return data.map((item: any) => ({
        imageIndices: item.imageIndices || [],
        productData: {
          title: item.title || "",
          description: item.description || "",
          price: item.price || 0,
          currency: "NGN",
          category: item.category || "General",
          tags: item.tags || [],
          images: [],
          colors: item.colors || [],
          sizes: item.sizes || [],
          materials: item.materials || [],
          styles: item.styles || [],
          patterns: item.patterns || [],
          brand: item.brand || "Generic",
          condition: item.condition || "New",
          isDigital: item.isDigital || false,
          dimensions: item.dimensions || "",
          weight: item.weight || "",
        },
      }));
    } catch (error) {
      lastError = error as Error;
      console.error(`Error with ${name} API key:`, error);
      if (i < apiClients.length - 1) continue;
    }
  }

  console.error("All API keys failed. Last error:", lastError);
  return base64Images.map((_, idx) => ({
    imageIndices: [idx],
    productData: {
      title: "Awesome Product",
      description: "A high-quality item ready for a new home. #exclusive",
      price: 5000,
      currency: "NGN",
      category: "General",
      tags: ["#sale", "#new"],
      colors: [],
      sizes: [],
      materials: [],
      styles: [],
      patterns: [],
      brand: "Generic",
      condition: "New",
      isDigital: false,
      dimensions: "",
      weight: "",
    },
  }));
};

// Initialize Gemini Clients with different API keys
const apiClients = [
  { client: new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY }), name: "primary" },
  { client: new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY_A }), name: "fallback1" },
  { client: new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY_B }), name: "fallback2" }
];

export const analyzeProductImage = async (base64Image: string): Promise<Partial<ProductData>> => {
  // Define the schema for structured output
  const productSchema: Schema = {
    type: Type.OBJECT,
    properties: {
      title: {
        type: Type.STRING,
        description: "The EXACT real product name as identified from labels, packaging, or tags in the image. NEVER make up generic titles.",
      },
      description: {
        type: Type.STRING,
        description: "A proper product description (2-3 sentences). Write it like a seller would: what the product is, what it does, key features, benefits, materials. NEVER describe the image or photo itself. NEVER start with 'This image shows...' or 'I can see...'.",
      },
      price: {
        type: Type.NUMBER,
        description: "A realistic estimated market price in Nigerian Naira (NGN). Example: 5000 for ₦5,000.",
      },
      category: {
        type: Type.STRING,
        description: "The actual category of the product (e.g., Fashion, Electronics, Beauty, Home, Books, Digital, Service). Be specific.",
      },
      tags: {
        type: Type.ARRAY,
        items: { type: Type.STRING },
        description: "3-5 specific, relevant tags describing the actual product.",
      },
      brand: { type: Type.STRING, description: "The ACTUAL brand name visible on the product or packaging. Only use 'Generic' if absolutely no brand is visible anywhere." },
      condition: { 
          type: Type.STRING, 
          enum: ["New", "Used", "Refurbished"],
          description: "The likely condition of the item based on visual cues." 
      },
      colors: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Visual colors detected.",
      },
      sizes: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Likely sizes if apparel (e.g. S, M, L).",
      },
      materials: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Likely materials (e.g. Cotton, Leather, Metal).",
      },
      styles: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Style descriptors (e.g. Vintage, Modern, Streetwear).",
      },
      patterns: {
          type: Type.ARRAY,
          items: { type: Type.STRING },
          description: "Pattern descriptors (e.g. Striped, Solid, Floral).",
      },
      isDigital: {
          type: Type.BOOLEAN,
          description: "True if this looks like a digital product, software, or service card.",
      },
      dimensions: { type: Type.STRING, description: "Estimated dimensions if applicable (e.g. '10x10cm'), or empty." },
      weight: { type: Type.STRING, description: "Estimated weight if applicable (e.g. '0.5kg'), or empty." }
    },
    required: ["title", "description", "price", "category", "tags"],
  };

  // Strip the data URL prefix if present
  const base64Data = base64Image.replace(/^data:image\/\w+;base64,/, "");

  // Try each API key in sequence until one works
  let lastError: Error | null = null;
  
  for (let i = 0; i < apiClients.length; i++) {
    const { client, name } = apiClients[i];
    
    try {
      console.log(`Attempting with ${name} API key...`);
      
      const response = await client.models.generateContent({
        model: "gemini-2.5-flash",
        contents: {
          parts: [
            {
              inlineData: {
                mimeType: "image/jpeg",
                data: base64Data,
              },
            },
            {
              text: "You are Meo, an expert Nigerian social commerce listing generator. Identify the EXACT product from the image: read labels, logos, or deduce from physical features. Then output a SALES DESCRIPTION, not an image analysis. Description must describe what the product IS/DOES (features, benefits, materials). NEVER describe the photo itself. NEVER start with 'This image shows' or 'I can see'. Title MUST be the real product name, never generic. Price in NGN.",
            },
          ],
        },
        config: {
          responseMimeType: "application/json",
          responseSchema: productSchema,
        },
      });

      const text = response.text;
      if (!text) {
        throw new Error("No text returned from Gemini");
      }

      const data = JSON.parse(text);
      console.log(`Successfully used ${name} API key`);
      return data;

    } catch (error) {
      lastError = error as Error;
      console.error(`Error with ${name} API key:`, error);
      
      // If this wasn't the last key, continue to next one
      if (i < apiClients.length - 1) {
        console.log(`Retrying with next API key...`);
        continue;
      }
    }
  }

  // If we've exhausted all API keys and all failed, log and return fallback data
  console.error("All API keys failed. Last error:", lastError);
  console.error("Returning fallback 'Awesome Product' data");
  
  return {
    title: "Awesome Product",
    description: "A high-quality item ready for a new home. #exclusive",
    price: 5000,
    currency: "NGN",
    category: "General",
    tags: ["#sale", "#new"],
    colors: [],
    sizes: [],
    materials: [],
    styles: [],
    patterns: [],
    brand: "Generic",
    condition: "New",
    isDigital: false,
    stock: 1,
    variants: []
  };
};